<template>
    <div class="learning-admin-path-edit">
        <l-headline
            :title="path.name || $t(`learning.admin.path.details.title`)"
            :sub-title="$t(`learning.admin.path.details.subTitle`)"
        ></l-headline>

        <div>
            <!-- Basics -->
            <div v-show="step === 'basics'" data-test-view="learning-admin-path-edit-basics">
                <div class="row">
                    <div class="col-md-12">
                        <l-input-text
                            ref="basics-input-name"
                            v-model="path.name"
                            type="text"
                            :label="''"
                            :placeholder="$t(`learning.admin.path.details.placeholder.name`)"
                            data-test-id="learning-path-basics-name"
                        ></l-input-text>
                        <l-input-text
                            ref="basics-input-description"
                            v-model="path.description"
                            type="textarea"
                            :optional="true"
                            :label="''"
                            :placeholder="$t(`learning.admin.path.details.placeholder.description`)"
                            data-test-id="learning-path-basics-description"
                        ></l-input-text>

                        <hr />

                        <!-- Error -->
                        <div v-if="error">
                            <l-text-alert
                                type="danger"
                                :text="$t(`learning.admin.path.details.error.${error.status}`)"
                            />
                        </div>
                        <!--/Error -->

                        <div class="text-center">
                            <button
                                class="btn btn-primary btn-raised"
                                data-test-id="learning-path-basics-save"
                                @click="
                                    () =>
                                        onSave({
                                            to: $router.currentRoute.path.replace(
                                                '/basics',
                                                '/steps',
                                            ),
                                        })
                                "
                            >
                                {{ $t(`learning.admin.path.details.btn.save`) }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Steps -->
            <div
                v-show="step === 'steps'"
                class="learning-admin-path-edit-steps"
                data-test-view="learning-admin-path-edit-steps"
            >
                <div v-show="!path.steps.length">
                    <l-no-data :text="$t(`learning.admin.path.details.steps.searchStart`)" />
                </div>
                <div v-show="path.steps.length" class="step-timeline">
                    <div
                        v-b-popover.hover.righttop="{
                            content: $t('learning.admin.path.details.steps.timeline.start'),
                        }"
                        class="step-timeline-start"
                    >
                        <span>
                            <i class="step-icon fa fa-flag-checkered"></i>
                        </span>
                    </div>

                    <draggable
                        v-model="path.steps"
                        :disabled="isUpdatingStepOrder"
                        @change="updateStepOrder"
                    >
                        <transition-group>
                            <div
                                v-for="(step, index) in path.steps"
                                :key="step.learningElementCommonId"
                                :class="[
                                    'step-container',
                                    { 'step-container-optional': step.optional },
                                ]"
                                data-test-class="step-container"
                            >
                                <div class="index">
                                    <span>
                                        <i
                                            :class="[
                                                'step-icon',
                                                'fa',
                                                `fa-${getIconFromType(step.learningElement.type)}`,
                                            ]"
                                        ></i>
                                    </span>
                                </div>
                                <div class="step">
                                    <span
                                        class="step-name skill-title fake-link clickable ellipsis"
                                        tabindex="0"
                                        @click="goToEditElement(step.learningElement)"
                                        @keyup.enter="goToEditElement(step.learningElement)"
                                    >
                                        {{ step.learningElement.name }}
                                    </span>

                                    <label
                                        v-b-popover.hover.left="{
                                            content: $t(
                                                'learning.admin.path.details.steps.tooltip.optional',
                                            ),
                                        }"
                                        class="step-optional clickable"
                                        for="step-optional-check"
                                    >
                                        <input
                                            id="step-optional-check"
                                            v-model="step.optional"
                                            type="checkbox"
                                        />
                                        <span class="optional-label">{{
                                            $t("learning.admin.path.details.label.optional")
                                        }}</span>
                                    </label>
                                    <i
                                        class="fa fa-trash clickable color-primary step-delete"
                                        tabindex="0"
                                        @click="() => deleteStep(index)"
                                        @keyup.enter="() => deleteStep(index)"
                                    ></i>
                                </div>
                                <div
                                    v-b-popover.hover.right="{
                                        content: $t(
                                            'learning.admin.path.details.steps.tooltip.duration',
                                        ),
                                    }"
                                    class="step-duration"
                                >
                                    <label
                                        class="duration-label clickable"
                                        :for="`duration-input-${index}`"
                                    >
                                        <i class="fa fa-hourglass-half"></i>
                                    </label>
                                    <l-input-text
                                        :id="`duration-input-${index}`"
                                        v-model="step.duration"
                                        type="number"
                                        :optional="true"
                                        :label="''"
                                        wrapper-class="duration-wrapper"
                                        input-class="duration-input"
                                        :data-test-id="
                                            `learning-path-steps-duration-input-${index}`
                                        "
                                    ></l-input-text>
                                    <span class="duration-metric">{{
                                        $t("learning.admin.path.details.label.days")
                                    }}</span>
                                </div>
                            </div>
                        </transition-group>
                    </draggable>

                    <div
                        v-b-popover.hover.right="{
                            content: $t('learning.admin.path.details.steps.timeline.end'),
                        }"
                        class="step-timeline-end"
                    >
                        <span>
                            <i class="step-icon fa fa-graduation-cap"></i>
                        </span>
                    </div>
                </div>

                <div class="text-center">
                    <button
                        class="btn btn-primary btn-framed btn-framed-primary"
                        data-test-id="learning-path-steps-add"
                        @click="addNewStep"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("learning.admin.path.details.btn.addStep") }}
                    </button>
                </div>

                <hr />

                <!-- Error -->
                <div v-if="error">
                    <l-text-alert
                        type="danger"
                        :text="$t(`learning.admin.path.details.error.${error.status}`)"
                    />
                </div>
                <!--/Error -->

                <div
                    v-b-popover.hover.bottom="{
                        content: $t('learning.admin.path.details.steps.tooltip.saveDisabled'),
                        disabled: path.steps.length,
                    }"
                    class="text-center"
                >
                    <button
                        class="btn btn-primary btn-raised"
                        :disabled="!path.steps.length"
                        data-test-id="learning-path-steps-save"
                        @click="
                            () =>
                                onSave({
                                    to: $router.currentRoute.path.replace('/steps', '/targeting'),
                                })
                        "
                    >
                        {{ $t(`learning.admin.path.details.btn.save`) }}
                    </button>
                </div>
            </div>

            <!-- Targeting -->
            <div
                v-show="step === 'targeting'"
                class="learning-admin-path-edit-targeting"
                data-test-view="learning-admin-path-edit-targeting"
            >
                <h4 class="color-dark">
                    <strong>{{
                        $t("learning.admin.path.details.targeting.participants.title")
                    }}</strong>
                </h4>
                <form name="participants" class="form-group radio-form" style="margin-top: 20px">
                    <label for="path-access-targeting-mode-none">
                        <input
                            id="path-access-targeting-mode-none"
                            v-model="path.access.targeting.mode"
                            type="radio"
                            value="none"
                            data-test-id="learning-path-targeting-participants-none"
                        />

                        <span class="color-dark">{{
                            $t("learning.admin.path.details.targeting.participants.label.none")
                        }}</span>
                    </label>
                    <label for="path-access-targeting-mode-all">
                        <input
                            id="path-access-targeting-mode-all"
                            v-model="path.access.targeting.mode"
                            type="radio"
                            value="all"
                            data-test-id="learning-path-targeting-participants-all"
                        />

                        <span class="color-dark">{{
                            $t("learning.admin.path.details.targeting.participants.label.all")
                        }}</span>
                    </label>
                    <label for="path-access-targeting-mode-selected">
                        <input
                            id="path-access-targeting-mode-selected"
                            v-model="path.access.targeting.mode"
                            type="radio"
                            value="selectedParticipants"
                            data-test-id="learning-path-targeting-participants-selected"
                        />

                        <span class="color-dark">{{
                            $t("learning.admin.path.details.targeting.participants.label.selected")
                        }}</span>
                    </label>

                    <div v-show="path.access.targeting.mode === 'selectedParticipants'">
                        <l-input-typeahead
                            :label="''"
                            :placeholder="
                                $t(
                                    `learning.admin.path.details.targeting.participants.placeholder.selected`,
                                )
                            "
                            :data="participants"
                            :on-select="typeaheadParticipantsOnSelect"
                            :on-input-change="typeaheadParticipantsOnInputChange"
                        >
                        </l-input-typeahead>
                        <span
                            v-for="(participant, index) in path.participants"
                            v-show="path.participants.length"
                            :key="participant._id"
                        >
                            <span
                                class="label label-default"
                                tabindex="0"
                                @click="removeSelectedParticipant(index)"
                                @keyup.enter="removeSelectedParticipant(index)"
                            >
                                {{ participant.name }} <i class="fa fa-remove"></i>
                            </span>
                        </span>
                    </div>
                </form>

                <h4 v-show="path.access.targeting.mode !== 'none'" class="color-dark">
                    <strong>{{ $t("learning.admin.path.details.targeting.kickoff.title") }}</strong>
                </h4>

                <form
                    v-show="path.access.targeting.mode !== 'none'"
                    name="targeting"
                    class="form-group radio-form"
                    style="margin-top: 20px"
                >
                    <label for="path-access-targeting-timing-radio-dynamic">
                        <input
                            id="path-access-targeting-timing-radio-dynamic"
                            v-model="path.access.targeting.timing"
                            type="radio"
                            value="dynamic"
                            data-test-id="learning-path-targeting-kickoff-dynamic"
                        />

                        <span class="color-dark">
                            {{
                                $t(
                                    "learning.admin.path.details.targeting.kickoff.label.duration.title",
                                )
                            }}
                        </span>
                    </label>
                    <div class="targeting-duration">
                        <l-input-text
                            v-show="path.access.targeting.timing === 'dynamic'"
                            v-model="path.access.targeting.hireDateDelay.duration"
                            type="number"
                            input-class="targeting-duration-input"
                            :label="''"
                            data-test-id="learning-path-targeting-kickoff-duration-input"
                        />
                        <span
                            v-show="path.access.targeting.timing == 'dynamic'"
                            class="color-dark"
                            >{{
                                $t(
                                    "learning.admin.path.details.targeting.kickoff.label.duration.weeks",
                                )
                            }}</span
                        >
                    </div>

                    <label for="path-access-targeting-timing-radio-immediate">
                        <input
                            id="path-access-targeting-timing-radio-immediate"
                            v-model="path.access.targeting.timing"
                            type="radio"
                            value="immediate"
                            data-test-id="learning-path-targeting-kickoff-immediate"
                        />

                        <span class="color-dark">
                            {{
                                $t(
                                    "learning.admin.path.details.targeting.kickoff.label.immediately",
                                )
                            }}
                        </span>
                    </label>

                    <label for="path-access-targeting-timing-radio-oneOff">
                        <input
                            id="path-access-targeting-timing-radio-oneOff"
                            v-model="path.access.targeting.timing"
                            type="radio"
                            value="oneOff"
                            data-test-id="learning-path-targeting-kickoff-oneOff"
                        />

                        <span class="color-dark">
                            {{
                                $t(
                                    "learning.admin.path.details.targeting.kickoff.label.oneoff.title",
                                )
                            }}
                        </span>
                        <div v-show="path.access.targeting.timing === 'oneOff'">
                            <l-date-picker
                                :label="''"
                                :placeholder="''"
                                :value="new Date(path.access.targeting.oneOff.date)"
                                :on-date-change="date => (path.access.targeting.oneOff.date = date)"
                                data-test-id="learning-path-targeting-kickoff-oneOff-date"
                            >
                            </l-date-picker>
                        </div>
                    </label>
                </form>

                <hr />

                <h4 class="color-dark">
                    <strong>
                        {{ $t("learning.admin.path.details.targeting.selfenrol.title") }}
                    </strong>
                </h4>
                <form name="self-enrol" class="form-group radio-form tw-mt-5">
                    <label for="path-access-self-enrol-mode-none">
                        <input
                            id="path-access-self-enrol-mode-none"
                            v-model="path.access.selfenrol.mode"
                            type="radio"
                            value="none"
                            data-test-id="learning-path-self-enrol-mode-none"
                        />

                        <span class="color-dark">
                            {{ $t("learning.admin.path.details.targeting.selfenrol.options.no") }}
                        </span>
                    </label>
                    <label for="path-access-self-enrol-mode-all">
                        <input
                            id="path-access-self-enrol-mode-all"
                            v-model="path.access.selfenrol.mode"
                            type="radio"
                            value="all"
                            data-test-id="learning-path-self-enrol-mode-all"
                        />

                        <span class="color-dark">
                            {{ $t("learning.admin.path.details.targeting.selfenrol.options.all") }}
                        </span>
                    </label>
                    <label for="path-access-self-enrol-mode-skills">
                        <input
                            id="path-access-self-enrol-mode-skills"
                            v-model="path.access.selfenrol.mode"
                            type="radio"
                            value="targeting"
                            data-test-id="learning-path-selfenrol-mode-skills"
                        />

                        <span class="color-dark">
                            {{
                                $t(
                                    "learning.admin.path.details.targeting.selfenrol.options.targeting",
                                )
                            }}
                        </span>
                    </label>
                </form>

                <l-input-typeahead
                    v-show="path.access.selfenrol.mode == 'targeting'"
                    :show-picture="false"
                    :label="''"
                    :placeholder="
                        $t('learning.admin.path.details.targeting.selfenrol.competencyPlaceholder')
                    "
                    :data="untargetedCompetencies"
                    :on-select="typeaheadCompetenciesOnSelect"
                    :on-input-change="function() {}"
                >
                </l-input-typeahead>

                <span
                    v-for="competency in targetedCompetencies"
                    v-show="targetedCompetencies.length"
                    :key="competency._id"
                >
                    <span
                        class="label label-default"
                        tabindex="0"
                        @click="removeSelectedCompetency(competency._id)"
                        @keyup.enter="removeSelectedCompetency(competency._id)"
                    >
                        {{ competency.name }} <i class="fa fa-remove"></i>
                    </span>
                </span>

                <hr />
                <!-- Error -->
                <div v-if="error">
                    <l-text-alert
                        type="danger"
                        :text="$t(`learning.admin.path.details.error.${error.status}`)"
                    />
                </div>
                <!--/Error -->

                <div class="text-center">
                    <button
                        class="btn btn-primary btn-raised"
                        data-test-id="learning-path-targeting-save"
                        @click="
                            () =>
                                onSave({
                                    to: $router.currentRoute.path.replace('/targeting', '/status'),
                                })
                        "
                    >
                        {{ $t(`learning.admin.path.details.btn.save`) }}
                    </button>
                </div>
            </div>

            <!-- Status -->
            <div
                v-show="step === 'status'"
                class="learning-admin-path-edit-status"
                data-test-view="learning-admin-path-edit-status"
            >
                <div class="row">
                    <div class="col-md-6">
                        <h4 class="color-dark">
                            {{ $t("learning.admin.path.details.status.title") }}
                        </h4>
                        <p>
                            {{
                                $t(`learning.admin.path.details.status.description.${path.status}`)
                            }}
                        </p>
                    </div>
                    <div class="col-md-6">
                        <h4 class="color-dark">
                            {{ $t("learning.admin.path.details.version.title") }}
                        </h4>
                        <p>
                            {{
                                $t("learning.admin.path.details.version.description", {
                                    version: path.version || 0,
                                })
                            }}
                        </p>
                    </div>
                </div>

                <hr />

                <p>{{ $t("learning.admin.path.details.status.explanation") }}</p>

                <hr />

                <!-- Error -->
                <div v-if="error">
                    <l-text-alert
                        type="danger"
                        :text="$t(`learning.admin.path.details.error.${error.status}`)"
                    />
                </div>
                <!--/Error -->

                <div class="text-right footer">
                    <button
                        class="btn btn-raised"
                        data-test-id="learning-path-status-disable"
                        @click="showConfirmDisableModal = true"
                    >
                        {{ $t(`learning.admin.path.details.btn.disable`) }}
                    </button>
                    <button
                        class="btn btn-primary btn-raised"
                        data-test-id="learning-path-status-activate"
                        @click="
                            () => {
                                path.status = 'active';
                                onSave({
                                    to: $router.currentRoute.path.replace(
                                        '/edit/status',
                                        '/dashboard',
                                    ),
                                });
                            }
                        "
                    >
                        {{ $t(`learning.admin.path.details.btn.activate`) }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Add Step Modal -->
        <l-modal v-show="showStepModal" size="l" @close="closeStepModal">
            <template #header>{{ stepModalTitle }}</template>
            <template #body>
                <router-view :add-element-to-step="addElementToStep"></router-view>
            </template>
        </l-modal>
        <!--/ Add Step Modal -->

        <!-- Confirm Disable -->
        <l-modal
            v-show="showConfirmDisableModal"
            size="m"
            class="confirm-disable-modal"
            @close="closeConfirmDisableModal"
        >
            <template #header>{{
                $t(`learning.admin.path.details.status.modal.disable.title`, { name: path.name })
            }}</template>
            <template #body>
                <p>
                    {{
                        $t(`learning.admin.path.details.status.modal.disable.description`, {
                            name: path.name,
                        })
                    }}
                </p>
                <hr />

                <div class="footer">
                    <button
                        class="btn btn-raised"
                        data-test-id="learning-admin-path-disable-cancel"
                        @click="closeConfirmDisableModal"
                    >
                        {{ $t("learning.admin.path.details.status.modal.disable.btn.cancel") }}
                    </button>
                    <button
                        class="btn btn-danger btn-raised"
                        data-test-id="learning-admin-path-disable-confirm"
                        @click="onDisable"
                    >
                        {{ $t("learning.admin.path.details.status.modal.disable.btn.confirm") }}
                    </button>
                </div>
            </template>
        </l-modal>
        <!--/Confirm Disable -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import Draggable from "vuedraggable";
import LDatePicker from "@/components/LDatePicker";

import duration from "@/utils/iso8601-duration";

import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LModal from "@/components/LModal";
import LNoData from "@/components/LNoData";
import LTextAlert from "@/components/LTextAlert";

import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        Draggable,
        LDatePicker,
        LHeadline,
        LInputText,
        LInputTypeahead,
        LModal,
        LNoData,
        LTextAlert,
    },
    props: {
        pathId: {
            type: String,
            required: true,
        },
        step: {
            type: String,
        },
    },
    data() {
        return {
            showStepModal: false,
            stepModalTitle: "",
            showConfirmDisableModal: false,
            path: {
                participants: [], // Added for reactivity
            },
            isUpdatingStepOrder: false,
            error: null,
        };
    },
    computed: {
        ...mapGetters({
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            getPath: "learning/path/details",
            getMatrix: "skills/getMatrix",
        }),
        participants() {
            return [
                ...this.usersSearchList.map(user => ({
                    _id: user._id,
                    type: "user",
                    firstname: user.firstname,
                    lastname: user.lastname,
                    name: user.firstname + " " + user.lastname,
                    desc: user.teamRole.title,
                    picture: user.picture,
                })),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                    desc: this.$t("typeahead.team"),
                    picture: teamPicture,
                })),
            ];
        },
        skills() {
            return this.getMatrix().map(item => ({
                _id: item._id,
                name: item.name,
                desc: item.categoryName,
            }));
        },
        untargetedCompetencies() {
            return this.skills.filter(
                competency =>
                    !(this.path?.access?.selfenrol?.targeting?.skills?.include || []).includes(
                        competency._id,
                    ),
            );
        },
        targetedCompetencies() {
            return this.skills.filter(competency =>
                (this.path?.access?.selfenrol?.targeting?.skills?.include || []).includes(
                    competency._id,
                ),
            );
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.showStepModal = newRoute.meta && newRoute.meta.modal;
                this.stepModalTitle = this.getModalTitleFromRoute(newRoute);
                this.path = this.formatPath();
                this.$emit("new", this.path.isNew);
            },
        },
    },
    created() {
        this.$store.dispatch("skills/getMatrix", { payload: { matrixMode: "all" } });
        // Store subscription
        this.unsubscribe = this.$store.subscribe(mutation => {
            // We need to subscribe to the store's mutation and fetch the data from the store
            // manually as reactivity doesn't work so easily with nested values.
            //
            if (mutation.type === "learning/path/updateDetails") {
                this.path = this.formatPath();
                this.$emit("new", this.path.isNew);
            }
        });
    },
    beforeDestroy() {
        // Unsubsribe from store
        this.unsubscribe();
    },
    methods: {
        formatPath() {
            const path = this.getPath(this.pathId);

            // Format participants to be used in the typeahead
            path.participants = [
                ...path.access.targeting.users.include.map(user => ({
                    ...user,
                    type: "user",
                    name: user.fullname,
                })),
                ...path.access.targeting.teams.include.map(team => ({
                    ...team,
                    type: "team",
                })),
            ];

            // Format hire date duration
            path.access.targeting.hireDateDelay.duration = duration.parse(
                path.access.targeting.hireDateDelay.duration,
            );

            // Format steps duration
            path.steps = (path.steps || []).map(step => {
                const prevStepDuration = this.path?.steps?.find(
                    s => s.learningElementCommonId === step.learningElementCommonId,
                )?.duration;
                const newStepDuration = duration.parse(step.duration, "days");

                return {
                    ...step,
                    duration: newStepDuration || prevStepDuration || 0,
                };
            });

            return path;
        },

        typeaheadParticipantsOnSelect(participant) {
            if (!this.path.participants.find(p => p._id === participant._id)) {
                this.path.participants.push(participant);
            }
            this.$store.dispatch("users/search/reset");
            this.$store.dispatch("teams/search/reset");
        },
        typeaheadParticipantsOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        typeaheadCompetenciesOnSelect(competency) {
            if (
                !(this.path?.access?.selfenrol?.targeting?.skills?.include || []).includes(
                    competency._id,
                )
            ) {
                (this.path?.access?.selfenrol?.targeting?.skills?.include || []).push(
                    competency._id,
                );
            }
        },
        removeSelectedCompetency(removedCompetencyId) {
            this.path.access.selfenrol.targeting.skills.include = (
                this.path?.access?.selfenrol?.targeting?.skills?.include || []
            ).filter(competency => competency != removedCompetencyId);
        },
        removeSelectedParticipant(index) {
            this.path.participants.splice(index, 1);
        },

        addNewStep() {
            this.$router.push(`/learning/admin/path/${this.pathId}/edit/steps/add`);
        },
        async addElementToStep({ commonId, name, type }) {
            // Disallow dupicates
            if (!this.path.steps.find(step => step.learningElementCommonId === commonId)) {
                this.path.steps.push({
                    duration: 0,
                    learningElementCommonId: commonId,
                    learningElement: { commonId, name, type },
                });

                // Save path with new step
                await this.onSave({ to: `/learning/admin/path/${this.pathId}/edit/steps` });
            } else {
                this.closeStepModal();
            }
        },
        async updateStepOrder() {
            this.isUpdatingStepOrder = true;

            try {
                await this.onSave();
            } finally {
                this.isUpdatingStepOrder = false;
            }
        },
        async deleteStep(index) {
            this.path.steps.splice(index, 1);

            // Save path with new step
            await this.onSave();
        },
        closeStepModal() {
            if (this.$route.path.startsWith(`/learning/admin/path/${this.pathId}/edit/steps/`)) {
                this.$router.push(`/learning/admin/path/${this.pathId}/edit/steps`);
            }
        },
        closeConfirmDisableModal() {
            this.showConfirmDisableModal = false;
        },
        getModalTitleFromRoute(route) {
            switch (route.name) {
                case "LearningAdminPathEditStep":
                    return this.$t("learning.admin.path.details.steps.modal.title.new");
                case "LearningAdminElementEdit": {
                    const element = this.path.steps.find(
                        step => step.learningElementCommonId === route.params.elementId,
                    ).learningElement;
                    return this.$t("learning.admin.element.details.title.edit", {
                        name: element.name,
                    });
                }
                default:
                    return this.$t(`learning.admin.path.details.steps.modal.title.new`);
            }
        },
        getIconFromType(type) {
            switch (type) {
                case "article":
                    return "book";
                case "video":
                    return "play-circle";
                case "quiz":
                    return "question";
                case "task":
                    return "check";
                default:
                    return "pencil";
            }
        },

        goToEditElement({ commonId, type }) {
            this.$router.push(
                `/learning/admin/path/${this.pathId}/edit/steps/element/${type}/${commonId}/edit`,
            );
        },

        isValid(step) {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith(`${step}-input-`) ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },

        async onDisable() {
            await this.$store.dispatch({
                type: "learning/path/delete",
                payload: { id: this.path.commonId },
            });

            this.showConfirmDisableModal = false;

            this.$router.push("/learning/admin/path");
        },
        async onSave({ to } = {}) {
            if (!this.isValid(this.step)) {
                return;
            }

            this.error = null;

            const version = this.path.version == null ? 0 : this.path.version + 1;

            const path = {
                ...this.path,
                version,
                access: {
                    ...this.path.access,
                    targeting: {
                        oneOff: {
                            enabled: this.path.access.targeting.timing === "oneOff",
                            date: this.path.access.targeting.oneOff.date,
                        },
                        all: {
                            enabled: this.path.access.targeting.mode === "all",
                        },
                        hireDateDelay: {
                            enabled: this.path.access.targeting.timing === "dynamic",
                            duration: duration.convert(
                                this.path.access.targeting.hireDateDelay.duration,
                                "weeks",
                            ),
                        },
                        users: {
                            ...this.path.access.targeting.users,
                            include:
                                this.path.access.targeting.mode === "none"
                                    ? []
                                    : this.path.participants
                                          .filter(participant => participant.type === "user")
                                          .map(({ _id }) => _id),
                        },
                        teams: {
                            ...this.path.access.targeting.teams,
                            include:
                                this.path.access.targeting.mode === "none"
                                    ? []
                                    : this.path.participants
                                          .filter(participant => participant.type === "team")
                                          .map(({ _id }) => _id),
                        },
                    },
                },
                steps: (this.path.steps || []).map(step => ({
                    ...step,
                    duration: duration.convert(step.duration || 0, "days"),
                })),
            };

            delete path.participants;

            await this.$store
                .dispatch({
                    type: "learning/path/update",
                    payload: { path },
                })
                .then(() => {
                    if (to) {
                        this.$router.push(to);
                    }
                })
                .catch(err => {
                    if (err.response.status === 400 || err.response.status === 409) {
                        this.error = err.response;
                    } else {
                        this.error = {
                            ...err.response,
                            status: "generic",
                        };

                        throw err;
                    }
                });
        },
    },
};
</script>

<style scoped>
/* Label typeahead override */
.label.label-default {
    margin-right: 4px;
}

/* Step */
.step-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
}

.step-timeline-start,
.step-timeline-end {
    background: linear-gradient(var(--color-timeline-grey), var(--color-timeline-grey)) no-repeat
        center/2px 100%;
    align-self: stretch;
    position: relative;
    width: 50px;
    height: 50px;
    margin-right: 12px;
}
.step-timeline-start span,
.step-timeline-end span {
    background-color: white;
    height: 35px;
    width: 35px;
    text-align: center;
    padding-top: 5px;
    font-size: 1.33rem;
    position: absolute;
    border: 1px solid var(--color-timeline-grey);
    border-radius: 100%;
    left: 8px;
}
.step-timeline-end span {
    bottom: 0;
}

.index {
    background: linear-gradient(var(--color-timeline-grey), var(--color-timeline-grey)) no-repeat
        center/2px 100%;
    align-self: stretch;
    position: relative;
    width: 50px;
    margin-right: 12px;
}

.index span {
    background-color: white;
    height: 50px;
    width: 50px;
    text-align: center;
    padding-top: 13px;
    font-size: 1.67rem;
    position: absolute;
    top: 8px;
    border: 1px solid var(--color-timeline-grey);
    border-radius: 100%;
    cursor: move;
}
.index .step-icon {
    color: var(--color-grey);
    font-size: 2.2rem;
}

.step {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-grow: 1;
    padding: 2rem;
    margin-bottom: 6rem; /* We need extra space for the duration element */
    border: 1px solid var(--color-timeline-grey);
    border-radius: 12px;
}

.step-name {
    flex-grow: 1;
    max-width: 36vw; /* FIXME - Magic number ... */
}

.step-duration {
    display: flex;
    align-items: center;
    position: absolute;
    top: 80px;
    left: 8px;
}
.duration-label {
    background-color: white;
    color: var(--color-grey);
    height: 35px;
    width: 35px;
    padding-top: 6px;
    padding-left: 9px;
    border: 1px solid var(--color-timeline-grey);
    border-radius: 100%;
    margin-bottom: 0;
}

.duration-metric {
    padding-left: 10px;
}

.learning-admin-path-edit-steps >>> .duration-wrapper {
    padding: 0 !important;
    margin: 0 !important;
}
.learning-admin-path-edit-steps >>> .duration-input {
    margin: 0 4px 0 18px;
    width: 60px;
    text-align: right;
}

.step-optional {
    margin: 0 24px 0 12px;
}
.optional-label {
    color: var(--color-black);
}

.targeting-duration {
    display: flex;
    align-items: center;
}
.targeting-duration span {
    margin-left: 16px;
    margin-top: -30px;
}
.learning-admin-path-edit-targeting >>> .targeting-duration-input {
    width: 60px;
    text-align: right;
}

.learning-admin-path-edit-targeting h4,
.learning-admin-path-edit-status h4 {
    font-weight: 700;
}

.radio-form label {
    display: block;
    margin-bottom: 15px;
}

.radio-form label span {
    padding-left: 5px;
}

.learning-admin-path-edit-status .row h4 {
    margin-top: 0px;
}
.learning-admin-path-edit-status .footer .btn:first-of-type {
    margin-right: 12px;
}

/* Confirm Disable Modal */
.confirm-disable-modal p {
    white-space: pre-wrap;
}

.confirm-disable-modal .footer .btn:first-of-type {
    margin-right: 12px;
}
</style>
